import React, { useState } from 'react';
import {
  Box,
  Chip,
  Popover,
  TextField,
  Checkbox,
  Typography,
  IconButton,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { theme } from '../../styles/theme';

interface MultiColumnSelectItem {
  value: string | number;
  label: string;
  sublabel?: string;
  icon?: string;
  group?: string;
}

interface MultiColumnSelectProps {
  label: string;
  items: MultiColumnSelectItem[];
  value: (string | number)[];
  onChange: (value: (string | number)[]) => void;
  columns?: 2 | 3 | 4;
  chipColor?: 'primary' | 'secondary';
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  searchable?: boolean;
  groupBy?: boolean;
  selectAll?: boolean;
}

const MultiColumnSelect: React.FC<MultiColumnSelectProps> = ({
  label,
  items,
  value,
  onChange,
  columns = 3,
  chipColor = 'primary',
  placeholder,
  disabled = false,
  fullWidth = true,
  searchable = true,
  groupBy = false,
  selectAll = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Calculate sidebar width - using expanded width as default
  const sidebarWidth = 256; // This matches theme.sidebar.expandedWidth

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm('');
  };

  const handleToggleItem = (itemValue: string | number) => {
    const currentIndex = value.indexOf(itemValue);
    const newValue = [...value];

    if (currentIndex === -1) {
      newValue.push(itemValue);
    } else {
      newValue.splice(currentIndex, 1);
    }

    onChange(newValue);
  };

  const handleSelectAll = () => {
    if (value.length === filteredItems.length) {
      onChange([]);
    } else {
      onChange(filteredItems.map(item => item.value));
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const filteredItems = items.filter(item =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.sublabel && item.sublabel.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const groupedItems = groupBy && filteredItems.length > 0
    ? filteredItems.reduce((acc, item) => {
        const group = item.group || 'Other';
        if (!acc[group]) {
          acc[group] = [];
        }
        acc[group].push(item);
        return acc;
      }, {} as Record<string, MultiColumnSelectItem[]>)
    : { '': filteredItems };

  const open = Boolean(anchorEl);
  const id = open ? 'multi-column-popover' : undefined;

  const colorConfig = chipColor === 'primary'
    ? {
        bg: theme.colors.primary.light,
        text: theme.colors.text.primary,
        border: theme.colors.primary.main,
        hover: alpha(theme.colors.primary.main, 0.08),
      }
    : {
        bg: theme.colors.secondary.light,
        text: '#fff',
        border: theme.colors.secondary.main,
        hover: alpha(theme.colors.secondary.main, 0.08),
      };

  return (
    <>
      <Box
        sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}
      >
        <Box
          onClick={handleOpen}
          sx={{
            minHeight: 56,
            borderRadius: 1,
            border: `1px solid ${open ? colorConfig.border : theme.colors.border.light}`,
            padding: '16.5px 14px',
            cursor: disabled ? 'default' : 'pointer',
            transition: 'all 0.2s',
            backgroundColor: disabled ? alpha(theme.colors.border.light, 0.1) : 'transparent',
            '&:hover': !disabled ? {
              borderColor: colorConfig.border,
              backgroundColor: colorConfig.hover,
            } : {},
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: value.length === 0 ? 'center' : 'flex-start',
          }}
        >
          {value.length === 0 ? (
            <Typography
              sx={{
                color: theme.colors.text.secondary,
                fontFamily: theme.fonts.primary,
                fontSize: '1rem',
                userSelect: 'none',
              }}
            >
              {placeholder || `Select ${label ? label.toLowerCase() : 'items'}`}
            </Typography>
          ) : (
            value.map((val) => {
              const item = items.find(i => i.value === val);
              if (!item) return null;
              return (
                <Chip
                  key={val}
                  label={`${item.icon || ''} ${item.label}`}
                  size="small"
                  onDelete={(e) => {
                    e.stopPropagation();
                    handleToggleItem(val);
                  }}
                  sx={{
                    backgroundColor: colorConfig.bg,
                    color: colorConfig.text,
                    fontFamily: theme.fonts.primary,
                    '& .MuiChip-deleteIcon': {
                      color: colorConfig.text,
                      opacity: 0.7,
                      '&:hover': {
                        opacity: 1,
                      },
                    },
                  }}
                />
              );
            })
          )}
        </Box>
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: anchorEl?.offsetWidth,
            width: 'max-content',
            maxWidth: `calc(100vw - ${sidebarWidth + 100}px)`,
            maxHeight: '80vh',
            borderRadius: 2,
            boxShadow: theme.widget.boxShadow,
            ml: 1, // Add margin-left to push the dropdown away from the sidebar
            zIndex: 1301, // Higher than MUI drawer default (1200) to appear above sidebar
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {searchable && (
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={`Search ${label.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: theme.colors.text.secondary }} />,
                  endAdornment: searchTerm && (
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                  sx: {
                    fontFamily: theme.fonts.primary,
                  },
                }}
              />
            </Box>
          )}

          {selectAll && filteredItems.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                pb: 1.5,
                borderBottom: `1px solid ${theme.colors.border.light}`,
              }}
            >
              <Checkbox
                checked={value.length === filteredItems.length && filteredItems.length > 0}
                indeterminate={value.length > 0 && value.length < filteredItems.length}
                onChange={handleSelectAll}
                sx={{
                  color: theme.colors.secondary.main,
                  '&.Mui-checked': {
                    color: colorConfig.border,
                  },
                }}
              />
              <Typography
                sx={{
                  fontFamily: theme.fonts.primary,
                  fontSize: '0.875rem',
                  color: theme.colors.text.secondary,
                  cursor: 'pointer',
                }}
                onClick={handleSelectAll}
              >
                Select All ({filteredItems.length})
              </Typography>
            </Box>
          )}

          {filteredItems.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography
                sx={{
                  color: theme.colors.text.secondary,
                  fontFamily: theme.fonts.primary,
                }}
              >
                No items found
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 'calc(80vh - 200px)', overflowY: 'auto', overflowX: 'hidden' }}>
              {Object.entries(groupedItems).map(([group, groupItems]) => (
                <Box key={group}>
                  {group && (
                    <Typography
                      sx={{
                        fontFamily: theme.fonts.primary,
                        fontSize: '0.75rem',
                        color: theme.colors.text.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: 1,
                        py: 0.5,
                        backgroundColor: alpha(theme.colors.border.light, 0.3),
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {group}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${columns}, minmax(200px, 1fr))`,
                      gap: 1,
                      p: group ? 1 : 0,
                    }}
                  >
                    {groupItems.map((item) => {
                      const isSelected = value.includes(item.value);
                      return (
                        <Box
                          key={item.value}
                          onClick={() => handleToggleItem(item.value)}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            p: 1.5,
                            borderRadius: 1,
                            cursor: 'pointer',
                            backgroundColor: isSelected ? colorConfig.hover : 'transparent',
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: isSelected
                                ? alpha(colorConfig.border, 0.15)
                                : alpha(theme.colors.border.light, 0.5),
                            },
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            size="small"
                            sx={{
                              p: 0.5,
                              color: theme.colors.secondary.main,
                              '&.Mui-checked': {
                                color: colorConfig.border,
                              },
                            }}
                          />
                          <Box sx={{ ml: 0.5, flex: 1 }}>
                            <Typography
                              sx={{
                                fontFamily: theme.fonts.primary,
                                fontSize: '0.875rem',
                                fontWeight: isSelected ? 500 : 400,
                                color: theme.colors.text.primary,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              {item.icon && <span style={{ fontSize: '1.2em' }}>{item.icon}</span>}
                              <span>{item.label}</span>
                            </Typography>
                            {item.sublabel && (
                              <Typography
                                sx={{
                                  fontFamily: theme.fonts.primary,
                                  fontSize: '0.75rem',
                                  color: theme.colors.text.secondary,
                                  mt: 0.25,
                                }}
                              >
                                {item.sublabel}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default MultiColumnSelect;